import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import useRandomGlowEffect from '../hooks/useRandomGlowEffect';
import { getApiUrl } from '../config';
import { ParticipanteWeb, ParticipanteNoHit, ParticipanteStreamer } from '../types';
import MainHeader from '../components/MainHeader';

type FiltroRanking = 'general' | 'normal' | 'dificil';

const Home: React.FC = () => {
  const navigate = useNavigate();
  useRandomGlowEffect();
  const [rankingClassic, setRankingClassic] = useState<ParticipanteWeb[]>([]);
  const [rankingNoHit, setRankingNoHit] = useState<ParticipanteNoHit[]>([]);
  const [rankingStreamers, setRankingStreamers] = useState<ParticipanteStreamer[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroClassic, setFiltroClassic] = useState<FiltroRanking>('general');
  const [filtroNoHit, setFiltroNoHit] = useState<FiltroRanking>('general');
  const [loadingClassic, setLoadingClassic] = useState(false);
  const [loadingNoHit, setLoadingNoHit] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const [classicRes, nohitRes, streamersRes] = await Promise.allSettled([
        axios.get(getApiUrl('/api/ranking/classic')),
        axios.get(getApiUrl('/api/ranking/nohit')),
        axios.get(getApiUrl('/api/ranking/streamers')),
      ]);
      if (classicRes.status === 'fulfilled') setRankingClassic(classicRes.value.data);
      if (nohitRes.status === 'fulfilled') setRankingNoHit(nohitRes.value.data);
      if (streamersRes.status === 'fulfilled') setRankingStreamers(streamersRes.value.data);
      setLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (loading) return;
    setLoadingClassic(true);
    const url = filtroClassic === 'general'
      ? '/api/ranking/classic'
      : `/api/ranking/classic?dificultad=${filtroClassic}`;
    axios.get(getApiUrl(url))
      .then(r => setRankingClassic(r.data))
      .finally(() => setLoadingClassic(false));
  }, [filtroClassic]);

  useEffect(() => {
    if (loading) return;
    setLoadingNoHit(true);
    const url = filtroNoHit === 'general'
      ? '/api/ranking/nohit'
      : `/api/ranking/nohit?dificultad=${filtroNoHit}`;
    axios.get(getApiUrl(url))
      .then(r => setRankingNoHit(r.data))
      .finally(() => setLoadingNoHit(false));
  }, [filtroNoHit]);

  const formatFecha = (fecha: string) => {
    return new Date(fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  const getMedalColor = (pos: number) => {
    if (pos === 0) return 'text-yellow-400';
    if (pos === 1) return 'text-gray-300';
    if (pos === 2) return 'text-amber-600';
    return 'text-white/50';
  };

  return (
    <div className="min-h-screen bg-[#0a0a1a]">
      <MainHeader />

      {/* HERO */}
      <section className="text-center px-6 py-16 max-w-3xl mx-auto">
        <div className="flex justify-center mb-6">
          <img
            src="/logos/main-gamer-logo.png"
            alt="Main Gamer"
            className="main-gamer-logo"
            style={{ height: '140px' }}
          />
        </div>
        <p className="font-main text-xl text-white/70 mb-10 leading-relaxed">
          Bienvenidos a MainGamer — donde <span className="text-white font-bold">tú eres el jugador principal</span>.<br />
          Demuestra cuánto sabes de videojuegos.
        </p>
        <button
          onClick={() => navigate('/lobby')}
          className="btn-primary px-10 py-4 font-gaming text-sm tracking-wider shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50"
        >
          JUGAR AHORA
        </button>
      </section>

      {/* RANKING CLASSIC + RANKING NO HIT lado a lado */}
      <section className="px-6 pb-16 max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Ranking Classic */}
        <div>
          <h2
            data-text="RANKING CLASSIC"
            className="font-gaming text-base text-gaming-purple mb-3 tracking-widest text-center ranking-neon-holo ranking-neon-holo--a"
          >
            RANKING CLASSIC
          </h2>
          <div className="flex justify-center gap-2 mb-4">
            {(['general', 'normal', 'dificil'] as FiltroRanking[]).map(f => (
              <button
                key={f}
                onClick={() => setFiltroClassic(f)}
                className={`font-gaming text-xs px-3 py-1 rounded-full border transition-colors ${
                  filtroClassic === f
                    ? 'bg-gaming-purple border-gaming-purple text-white'
                    : 'border-white/20 text-white/40 hover:border-gaming-purple/60 hover:text-white/70'
                }`}
              >
                {f === 'general' ? 'GENERAL' : f === 'normal' ? 'NORMAL' : 'DIFÍCIL'}
              </button>
            ))}
          </div>
          <div className="card-gaming">
            {loading || loadingClassic ? (
              <div className="text-white/40 font-main text-center py-6">Cargando...</div>
            ) : rankingClassic.length === 0 ? (
              <div className="text-white/40 font-main text-center py-6">Aún no hay partidas</div>
            ) : (
              <div className="space-y-3">
                {rankingClassic.map((p, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className={`font-gaming text-sm w-6 text-right ${getMedalColor(i)}`}>
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-main font-bold text-white truncate">{p.nickname}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-gaming text-gaming-gold text-xs">{p.puntos}</p>
                      <span className={p.dificultad === 'dificil' ? 'badge-dificil' : 'badge-normal'}>
                        {p.dificultad}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Ranking No Hit */}
        <div>
          <h2
            data-text="RANKING NO HIT"
            className="font-gaming text-base text-gaming-neon-orange mb-3 tracking-widest text-center ranking-neon-holo ranking-neon-holo--b"
          >
            RANKING NO HIT
          </h2>
          <div className="flex justify-center gap-2 mb-4">
            {(['general', 'normal', 'dificil'] as FiltroRanking[]).map(f => (
              <button
                key={f}
                onClick={() => setFiltroNoHit(f)}
                className={`font-gaming text-xs px-3 py-1 rounded-full border transition-colors ${
                  filtroNoHit === f
                    ? 'bg-gaming-neon-orange border-gaming-neon-orange text-white'
                    : 'border-white/20 text-white/40 hover:border-gaming-neon-orange/60 hover:text-white/70'
                }`}
              >
                {f === 'general' ? 'GENERAL' : f === 'normal' ? 'NORMAL' : 'DIFÍCIL'}
              </button>
            ))}
          </div>
          <div className="card-gaming">
            {loading || loadingNoHit ? (
              <div className="text-white/40 font-main text-center py-6">Cargando...</div>
            ) : rankingNoHit.length === 0 ? (
              <div className="text-white/40 font-main text-center py-6">Aún no hay partidas</div>
            ) : (
              <div className="space-y-3">
                {rankingNoHit.map((p, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className={`font-gaming text-sm w-6 text-right ${getMedalColor(i)}`}>
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-main font-bold text-white truncate">{p.nickname}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-gaming text-gaming-cyan text-xs">Racha {p.aciertos}</p>
                      <span className={p.dificultad === 'dificil' ? 'badge-dificil' : 'badge-normal'}>
                        {p.dificultad}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* RANKING STREAMERS — centrado full width */}
      <section className="px-6 pt-2 pb-8 max-w-2xl mx-auto">
        <h2
          data-text="RANKING STREAMERS"
          className="font-gaming text-base text-gaming-cyan mb-4 tracking-widest text-center ranking-neon-holo ranking-neon-holo--c"
        >
          RANKING STREAMERS
        </h2>
        <div className="card-gaming">
          {loading ? (
            <div className="text-white/40 font-main text-center py-6">Cargando...</div>
          ) : rankingStreamers.length === 0 ? (
            <div className="text-white/40 font-main text-center py-6">Sin datos</div>
          ) : (
            <div className="space-y-3 overflow-y-auto max-h-[570px] pr-1">
              {rankingStreamers.map((s, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className={`font-gaming text-sm w-6 text-right ${getMedalColor(i)}`}>
                    {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-main font-bold text-white truncate">{s.concursante}</p>
                    {s.nombreCC && (
                      <p className="text-white/40 text-sm font-main truncate">{s.nombreCC.split(',')[0].replace(/\s*\([^)]*\)/g, '').trim()}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-gaming text-gaming-gold text-xs">{s.puntos}</p>
                    <span className={s.dificultad === 'dificil' ? 'badge-dificil' : 'badge-normal'}>
                      {s.dificultad}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/10 px-6 py-6 text-center text-white/30 text-sm font-main">
        <p>© 2026 MainGamer · Quizz videogames</p>
      </footer>
    </div>
  );
};

export default Home;
